import { Check, ChevronDown } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/utils/cn';

import {
  memberDirectoryTypesFromStatuses,
  MEMBER_DIRECTORY_TYPE_OPTIONS,
  type MemberDirectoryType,
  type MembersListState,
} from './members-list-state';

const GENDER_OPTIONS = ['female', 'male'] as const;
const MEMBERSHIP_SECTION_OPTIONS = ['active', 'inactive'] as const;

const FilterSection = ({ title, children }: { title: string; children: ReactNode }) => (
  <div className="py-1">
    <p className="px-2 pb-1 text-caption font-medium text-fg-secondary">{title}</p>
    {children}
  </div>
);

const FilterDivider = () => <div className="my-1 border-t border-border" role="separator" />;

const CheckboxRow = ({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={selected}
    onClick={onClick}
    className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-left text-body hover:bg-surface-sunken"
  >
    <span
      aria-hidden
      className={cn(
        'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border',
        selected ? 'border-brand bg-brand text-on-brand' : 'border-border-strong',
      )}
    >
      {selected ? <Check className="h-3 w-3" /> : null}
    </span>
    <span className="truncate text-fg">{label}</span>
  </button>
);

export const MembersDirectoryStatusFilter = ({
  state,
  onPatch,
  className,
}: {
  state: MembersListState;
  onPatch: (patch: Partial<MembersListState>) => void;
  className?: string;
}) => {
  const { t } = useTranslation('people');
  const [open, setOpen] = useState(false);

  const selectedTypes = memberDirectoryTypesFromStatuses(state.statuses);
  const membershipStatus = state.lifecycle;

  const patchFilters = (patch: Partial<MembersListState>) => {
    onPatch({ ...patch, page: 1, selectedId: null });
  };

  const setTypes = (types: MemberDirectoryType[]) => {
    patchFilters({
      statuses: types,
      lifecycle: membershipStatus,
    });
  };

  const setMembershipOption = (option: 'active' | 'inactive') => {
    patchFilters({
      lifecycle: membershipStatus === option ? 'any' : option,
    });
  };

  const triggerLabel = useMemo(() => {
    const parts: string[] = [];
    if (selectedTypes.length === 1) {
      parts.push(t(`members.views.${selectedTypes[0]}`));
    } else if (selectedTypes.length > 1) {
      parts.push(t('members.filters.statusSummary', { count: selectedTypes.length }));
    }
    if (state.gender) {
      parts.push(t(`members.gender.${state.gender}`));
    }
    if (membershipStatus === 'inactive') {
      parts.push(t('members.filters.membershipStatusOptions.inactive'));
    } else if (membershipStatus === 'active') {
      parts.push(t('members.filters.membershipStatusOptions.active'));
    }
    if (parts.length === 0) return t('members.filters.status');
    if (parts.length <= 2) return parts.join(', ');
    return t('members.filters.statusSummary', { count: parts.length });
  }, [membershipStatus, selectedTypes, state.gender, t]);

  const hasSelection =
    selectedTypes.length > 0 ||
    state.gender !== null ||
    membershipStatus === 'inactive' ||
    membershipStatus === 'active';

  const allTypesSelected =
    MEMBER_DIRECTORY_TYPE_OPTIONS.length > 0 &&
    MEMBER_DIRECTORY_TYPE_OPTIONS.every((type) => selectedTypes.includes(type));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label={t('members.filters.status')}
          className={cn(
            'flex h-8 min-h-8 w-auto min-w-[6.5rem] max-w-[10rem] items-center justify-between gap-2 rounded-md border border-border bg-surface-sunken/40 px-2.5 text-left text-body-sm hover:border-border-strong',
            className,
          )}
        >
          <span className="truncate text-fg">{triggerLabel}</span>
          <ChevronDown
            aria-hidden
            className={cn('h-4 w-4 shrink-0 text-fg-tertiary transition-transform', open && 'rotate-180')}
          />
        </button>
      </PopoverTrigger>

      <PopoverContent role="presentation" className="w-56 p-0">
        <div className="max-h-[min(22rem,var(--radix-popover-content-available-height))] overflow-y-auto p-1">
          <FilterSection title={t('members.filters.filterSections.memberStatus')}>
            {MEMBER_DIRECTORY_TYPE_OPTIONS.map((type) => (
              <CheckboxRow
                key={type}
                label={t(`members.views.${type}`)}
                selected={selectedTypes.includes(type)}
                onClick={() =>
                  setTypes(
                    selectedTypes.includes(type)
                      ? selectedTypes.filter((value) => value !== type)
                      : [...selectedTypes, type],
                  )
                }
              />
            ))}
          </FilterSection>

          <FilterDivider />

          <FilterSection title={t('members.filters.filterSections.gender')}>
            {GENDER_OPTIONS.map((gender) => (
              <CheckboxRow
                key={gender}
                label={t(`members.gender.${gender}`)}
                selected={state.gender === gender}
                onClick={() => patchFilters({ gender: state.gender === gender ? null : gender })}
              />
            ))}
          </FilterSection>

          <FilterDivider />

          <FilterSection title={t('members.filters.filterSections.membershipStatus')}>
            {MEMBERSHIP_SECTION_OPTIONS.map((option) => (
              <CheckboxRow
                key={option}
                label={t(`members.filters.membershipStatusOptions.${option}`)}
                selected={membershipStatus === option}
                onClick={() => setMembershipOption(option)}
              />
            ))}
          </FilterSection>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-2 py-1.5">
          <button
            type="button"
            onClick={() => patchFilters({ statuses: [], gender: null, lifecycle: 'any' })}
            disabled={!hasSelection}
            className="rounded px-2 py-1 text-body-sm text-fg-secondary hover:text-fg disabled:opacity-40"
          >
            {t('members.filters.statusClearAll')}
          </button>
          <button
            type="button"
            onClick={() => setTypes([...MEMBER_DIRECTORY_TYPE_OPTIONS])}
            disabled={allTypesSelected}
            className="rounded px-2 py-1 text-body-sm font-medium text-brand disabled:opacity-40"
          >
            {t('members.filters.statusSelectAll')}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
